import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { price as priceUtil } from '@eti/utils';
import { ScreenReaderPrice } from '@eti/components';
import { formatAmountInChargingCurrency, priceWithFullNameCurrency } from '../../../utils';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import type { RefundOfferExtendedType } from './models';

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

const nonRefundableStackStyles = css`
  padding-inline-start: 16px;
`;

const rowStyles = css`
  justify-content: space-between;
`;

interface PriceBreakdownProps {
  refundOffer: RefundOfferExtendedType;
}

const PriceBreakdown = ({ refundOffer }: PriceBreakdownProps) => {
  const { t } = useTranslation();
  const { language } = useSiteContext();
  const { paymentTypes, totalPriceIncludingAddOnOrders } = useOrderDetails();

  return (
    <>
      <Stack spacing={8}>
        <Inline className={rowStyles}>
          <Text variant={textVariants._16_BOLD}>{t('RefundAnalysis.TotalAmountPaid.Label')}</Text>
          <Text
            aria-hidden
            data-testid="refund-offer-total-amount-paid"
            variant={textVariants._16_BOLD}
          >
            {priceUtil.formatPrice(totalPriceIncludingAddOnOrders)}
          </Text>
          <ScreenReaderPrice price={priceUtil.formatPrice(totalPriceIncludingAddOnOrders)} />
        </Inline>
        <Inline className={rowStyles}>
          <Text>{t('RefundAnalysis.AirlinePenaltyFee.Title')}</Text>
          <Text aria-hidden data-testid="refund-offer-airline-penalty-fee">
            {priceUtil.formatPrice(refundOffer.airlineFee ? -refundOffer.airlineFee : 0)}
          </Text>
          <ScreenReaderPrice
            price={priceUtil.formatPrice(refundOffer.airlineFee ? -refundOffer.airlineFee : 0)}
          />
        </Inline>
        {refundOffer.nonRefundableProducts && refundOffer.nonRefundableProducts.length > 0 && (
          <>
            <Inline className={rowStyles}>
              <Text>{t('RefundAnalysis.NonRefundable.Title')}</Text>
              <Text aria-hidden data-testid="refund-offer-non-refundable-total-amount">
                {priceUtil.formatPrice(
                  refundOffer.totalAmountForNonRefundableProducts
                    ? -refundOffer.totalAmountForNonRefundableProducts
                    : 0,
                )}
              </Text>
              <ScreenReaderPrice
                price={priceUtil.formatPrice(
                  refundOffer.totalAmountForNonRefundableProducts
                    ? -refundOffer.totalAmountForNonRefundableProducts
                    : 0,
                )}
              />
            </Inline>
            <Stack className={nonRefundableStackStyles} role="list" spacing={8}>
              {refundOffer.nonRefundableProducts.map((nonRefundableProduct) => (
                <Inline className={rowStyles} key={nonRefundableProduct.code} role="listitem">
                  <Text>
                    {nonRefundableProduct.name ||
                      nonRefundableProduct.productSummaryAlternativeName}
                  </Text>
                  <Text
                    aria-hidden
                    data-testid={`refund-offer-${nonRefundableProduct.code}-non-refundable-amount`}
                  >
                    {priceUtil.formatPrice(-nonRefundableProduct.amount)}
                  </Text>
                  <ScreenReaderPrice price={priceUtil.formatPrice(-nonRefundableProduct.amount)} />
                </Inline>
              ))}
            </Stack>
          </>
        )}
        <Inline className={rowStyles}>
          <Text>{t('RefundAnalysis.HandlingFee.Label')}</Text>
          <Text aria-hidden data-testid="refund-offer-handling-fee">
            {priceUtil.formatPrice(refundOffer.handlingFee ? -refundOffer.handlingFee : 0)}
          </Text>
          <ScreenReaderPrice
            price={priceUtil.formatPrice(refundOffer.handlingFee ? -refundOffer.handlingFee : 0)}
          />
        </Inline>
      </Stack>
      <div className={dividerStyles} />
      <Stack spacing={8}>
        <Inline className={rowStyles}>
          <Text variant={textVariants._16_BOLD}>{t('RefundAnalysis.Title')}</Text>
          <Text aria-hidden data-testid="refund-offer-total-refund" variant={textVariants._16_BOLD}>
            {priceUtil.formatPrice(refundOffer.refundableAmount)}
          </Text>
          <ScreenReaderPrice price={priceUtil.formatPrice(refundOffer.refundableAmount)} />
        </Inline>
        {paymentTypes?.[0]?.name &&
          refundOffer.refundableAmount &&
          refundOffer.refundableAmount > 0 && (
            <Text data-testid="refund-offer-refund-method">{paymentTypes[0].name}</Text>
          )}
        {paymentTypes?.[0]?.maskedCardNumber &&
          refundOffer.refundableAmount &&
          refundOffer.refundableAmount > 0 && (
            <Text data-testid="refund-offer-card-number">{paymentTypes[0].maskedCardNumber}</Text>
          )}
      </Stack>
      {refundOffer.refundableAmountInChargingCurrency && (
        <>
          <div className={dividerStyles} />
          <Stack spacing={8}>
            <Inline className={rowStyles}>
              <Text variant={textVariants._16_BOLD}>
                {t(
                  'RefundAnalysis.RefundableAmountInChargingCurrency.Label',
                  refundOffer.refundableAmountInChargingCurrency.currency.code,
                )}
              </Text>
              <Text
                aria-hidden
                data-testid={`refund-offer-refundable-amount-in-${refundOffer.refundableAmountInChargingCurrency.currency.code}`}
                variant={textVariants._16_BOLD}
              >
                {formatAmountInChargingCurrency(
                  language.code,
                  refundOffer.refundableAmountInChargingCurrency.value,
                  refundOffer.refundableAmountInChargingCurrency.currency,
                )}
              </Text>
              <div className={screenReadersOnlyStyles}>
                {priceWithFullNameCurrency(
                  formatAmountInChargingCurrency(
                    language.code,
                    refundOffer.refundableAmountInChargingCurrency.value,
                    refundOffer.refundableAmountInChargingCurrency.currency,
                  ),
                  language.code,
                  refundOffer.refundableAmountInChargingCurrency.currency,
                )}
              </div>
            </Inline>
            <Text data-testid="void-refund-in-different-currency-message">
              {t(
                'RefundAnalysis.RefundProcessedInDifferentCurrency.Text',
                refundOffer.refundableAmountInChargingCurrency.currency.code,
              )}
            </Text>
          </Stack>
        </>
      )}
    </>
  );
};

export default PriceBreakdown;
